import React, { useEffect } from 'react'
import {
    Box,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import type { PipelineConfigTemplateListItem } from './usePipelineConfigApi'

export type PipelineConfigStartMode = 'template' | 'blank'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    templateOption: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        whiteSpace: 'normal',
    },
    templateDescription: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        lineHeight: 1.3,
    },
    templateSelectWrap: {
        position: 'relative',
    },
    loadingOverlay: {
        position: 'absolute',
        right: theme.spacing(1),
        top: '50%',
        transform: 'translateY(-50%)',
    },
}))

interface PipelineConfigCreateFieldsProps {
    name: string
    onNameChange: (value: string) => void
    startMode: PipelineConfigStartMode
    onStartModeChange: (mode: PipelineConfigStartMode) => void
    templateKey: string
    onTemplateKeyChange: (key: string) => void
    templates: PipelineConfigTemplateListItem[]
    templatesLoading?: boolean
    nameAutoFocus?: boolean
    namePlaceholder?: string
    nameError?: string | null
}

export const PipelineConfigCreateFields: React.FC<PipelineConfigCreateFieldsProps> = ({
    name,
    onNameChange,
    startMode,
    onStartModeChange,
    templateKey,
    onTemplateKeyChange,
    templates,
    templatesLoading = false,
    nameAutoFocus = true,
    namePlaceholder = 'e.g. Standard, Sign Fast post-lease screen',
    nameError = null,
}) => {
    const classes = useStyles()

    useEffect(() => {
        if (startMode !== 'template' || templates.length === 0) {
            return
        }
        if (
            !templateKey ||
            !templates.some((template) => template.key === templateKey)
        ) {
            onTemplateKeyChange(templates[0].key)
        }
    }, [startMode, templateKey, templates, onTemplateKeyChange])

    return (
        <Box className={classes.root}>
            <TextField
                autoFocus={nameAutoFocus}
                fullWidth
                variant="outlined"
                size="small"
                label="Configuration name"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder={namePlaceholder}
                error={Boolean(nameError)}
                helperText={nameError ?? undefined}
            />

            <FormControl component="fieldset">
                <RadioGroup
                    value={startMode}
                    onChange={(e) =>
                        onStartModeChange(
                            e.target.value as PipelineConfigStartMode,
                        )
                    }
                >
                    <FormControlLabel
                        value="template"
                        control={<Radio color="primary" size="small" />}
                        label="Start from template"
                    />
                    <FormControlLabel
                        value="blank"
                        control={<Radio color="primary" size="small" />}
                        label="Start blank"
                    />
                </RadioGroup>
                {startMode === 'blank' && (
                    <FormHelperText>
                        Not recommended — creates an empty pipeline with only
                        required steps.
                    </FormHelperText>
                )}
            </FormControl>

            {startMode === 'template' && (
                <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={templatesLoading || templates.length === 0}
                    className={classes.templateSelectWrap}
                >
                    <InputLabel id="pipeline-template-select-label">
                        Template
                    </InputLabel>
                    <Select
                        labelId="pipeline-template-select-label"
                        label="Template"
                        value={templateKey}
                        onChange={(e) =>
                            onTemplateKeyChange(String(e.target.value))
                        }
                    >
                        {templates.map((template) => (
                            <MenuItem key={template.key} value={template.key}>
                                <Box className={classes.templateOption}>
                                    <Typography variant="body2">
                                        {template.name}
                                    </Typography>
                                    {template.description ? (
                                        <Typography
                                            className={
                                                classes.templateDescription
                                            }
                                        >
                                            {template.description}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                    {templatesLoading && (
                        <CircularProgress
                            size={18}
                            className={classes.loadingOverlay}
                        />
                    )}
                </FormControl>
            )}
        </Box>
    )
}
